import type { ReactNode } from 'react'

import { DateField, Select, cn } from '@aeris/ui'

import {
  PERIOD_CHOICES,
  PERIOD_LABELS,
  type PeriodChoice,
} from '../lib/period-range.ts'

/**
 * Qual período, e de qual filial — F158.
 *
 * ## De onde veio
 *
 * Este bloco morava solto dentro de `store-dashboard.tsx`. Saiu de lá quando os
 * relatórios passaram a precisar da mesma pergunta: dois seletores de período
 * escritos separadamente divergem no primeiro ajuste — um ganha "Este ano", o
 * outro não; um valida as datas invertidas, o outro deixa passar — e a loja
 * aprende que a mesma pergunta se faz de dois jeitos.
 *
 * ## Só apresentação, de propósito
 *
 * Ele não guarda nada. Quem chama decide onde o estado mora, e os dois donos
 * escolheram diferente com razão:
 *
 * - o **painel** guarda em `localStorage`, porque é uma preferência de quem
 *   abre a tela toda manhã e não deve entrar na URL;
 * - os **relatórios** guardam na URL, porque um relatório se manda por
 *   WhatsApp como link e tem de voltar igual do outro lado.
 *
 * Um componente que persistisse por conta própria obrigaria os dois a aceitar a
 * mesma escolha.
 */

/** Os atalhos à vista, na ordem do app que o dono usa (F161). */
const QUICK_CHOICES: readonly (readonly [PeriodChoice, string])[] = [
  ['today', 'Hoje'],
  ['yesterday', 'Ontem'],
  ['last7', '7 dias'],
  ['last30', '30 dias'],
  ['month', 'Este mês'],
  ['custom', 'Escolher data'],
]

export type PeriodPickerProps = {
  choice: PeriodChoice
  onChoiceChange: (choice: PeriodChoice) => void
  /** As duas datas, em `AAAA-MM-DD`. Só valem quando a escolha é `custom`. */
  customFrom: string
  customTo: string
  onCustomFromChange: (day: string) => void
  onCustomToChange: (day: string) => void
  /**
   * As filiais que a pessoa pode escolher.
   *
   * Vazia ou com uma só, o seletor **não aparece**: uma loja de um endereço não
   * precisa responder "qual delas" toda vez.
   */
  branches?: readonly { id: string; name: string }[]
  branchId?: string
  onBranchChange?: (branchId: string) => void
  /**
   * "Qualquer data" como primeira opção (F161), para as listas que abrem sem
   * recorte — as Trocas. Marcada, ela manda sobre `choice`.
   */
  anyDate?: { readonly selected: boolean; readonly onSelect: () => void }
  /**
   * Os atalhos à vista, como botões, em vez de escondidos num seletor (F161).
   *
   * O dono mostrou o app que usa no celular: Hoje · Ontem · 7 dias · 30 dias,
   * um toque cada. No painel, que é aberto toda manhã, o período é a primeira
   * pergunta — e um seletor fechado esconde a resposta.
   */
  quick?: boolean
  /** O que vai à direita — "atualizado às 09:12", um aviso, nada. */
  children?: ReactNode
}

export const PeriodPicker = ({
  choice,
  onChoiceChange,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  branches = [],
  branchId = '',
  onBranchChange,
  anyDate,
  quick = false,
  children,
}: PeriodPickerProps): ReactNode => (
  <div className="flex flex-wrap items-end gap-2">
    {quick ? (
      <div
        role="group"
        aria-label="Período"
        className="flex flex-wrap gap-1 rounded-(--radius-md) border border-(--border-default) bg-(--surface-sunken) p-1"
      >
        {QUICK_CHOICES.map(([entry, label]) => (
          <button
            key={entry}
            type="button"
            aria-pressed={choice === entry}
            onClick={() => {
              onChoiceChange(entry)
            }}
            className={cn(
              'rounded-(--radius-sm) px-3 py-1.5 text-(length:--text-sm) font-(--weight-medium) transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)',
              choice === entry
                ? 'bg-(--surface-raised) text-(--text-primary) shadow-(--shadow-sm)'
                : 'text-(--text-secondary) hover:text-(--text-primary)'
            )}
          >
            {label}
          </button>
        ))}
      </div>
    ) : (
      <Select
        aria-label="Período"
        value={anyDate?.selected === true ? '' : choice}
        onChange={(event) => {
          if (event.target.value === '' && anyDate !== undefined) {
            anyDate.onSelect()
            return
          }
          onChoiceChange(event.target.value as PeriodChoice)
        }}
        className="w-44"
      >
        {anyDate === undefined ? null : <option value="">Qualquer data</option>}
        {PERIOD_CHOICES.map((entry) => (
          <option key={entry} value={entry}>
            {entry === 'custom' ? 'Escolher datas' : PERIOD_LABELS[entry]}
          </option>
        ))}
      </Select>
    )}

    {choice === 'custom' && anyDate?.selected !== true ? (
      <>
        <DateField
          aria-label="De"
          value={customFrom}
          onChange={(event) => {
            onCustomFromChange(event.target.value)
          }}
          className="w-40"
        />
        <DateField
          aria-label="Até"
          value={customTo}
          onChange={(event) => {
            onCustomToChange(event.target.value)
          }}
          className="w-40"
        />
      </>
    ) : null}

    {branches.length > 1 && onBranchChange !== undefined ? (
      <Select
        aria-label="Filial"
        value={branchId}
        onChange={(event) => {
          onBranchChange(event.target.value)
        }}
        className="w-48"
      >
        <option value="">Todas as filiais</option>
        {branches.map((entry) => (
          <option key={entry.id} value={entry.id}>
            {entry.name}
          </option>
        ))}
      </Select>
    ) : null}

    {children}
  </div>
)
